import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  LayoutDashboard, Inbox, Target, Building2, Users as UsersIcon, Contact2,
  Activity as ActivityIcon, Workflow, Settings as SettingsIcon, ShieldCheck,
  LogOut, ArrowLeft, ChevronDown,
} from "lucide-react";

const NAV_GROUPS = [
  { title: "Primary", items: [
    { to: "/admin",                end: true,  label: "Dashboard",      icon: LayoutDashboard },
    { to: "/admin/inbox",                      label: "Unified Inbox",  icon: Inbox },
    { to: "/admin/opportunities",              label: "Opportunities",  icon: Target },
    { to: "/admin/enterprise",                 label: "Enterprise",     icon: Building2 },
    { to: "/admin/contacts",                   label: "Contacts",       icon: Contact2 },
    { to: "/admin/users",                      label: "Users",          icon: UsersIcon },
    { to: "/admin/activity",                   label: "Activity",       icon: ActivityIcon },
    { to: "/admin/automation",                 label: "Automation",     icon: Workflow },
    { to: "/admin/settings",                   label: "Settings",       icon: SettingsIcon },
  ]},
];

export default function AdminShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [identityOpen, setIdentityOpen] = useState(false);
  const env = (typeof window !== "undefined" && window.__pl_env) || "local";
  const envTones = { local: "#34d399", staging: "#fcd34d", production: "#fda4af" };
  const envTone = envTones[env] || "#34d399";
  const primaryRole = (user?.roles || [])[0] || "admin";

  return (
    <div data-testid="admin-shell" style={{
      display: "grid", gridTemplateColumns: "262px 1fr", minHeight: "100vh",
      background: "var(--pl-bg)", color: "var(--pl-text)",
    }}>
      <aside style={{
        borderRight: "1px solid var(--pl-border)", background: "var(--pl-surface)",
        padding: 14, display: "flex", flexDirection: "column", overflowY: "auto",
      }}>
        {/* Brand wordmark */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 6px 14px" }}>
          <span style={{ width: 28, height: 28, borderRadius: 8, background: "linear-gradient(135deg, var(--pl-aqua), #B9FF2F)" }} />
          <div>
            <strong style={{ fontSize: 14, letterSpacing: "-0.02em" }}>PremierLab</strong>
            <div style={{ fontSize: 9.5, color: "var(--pl-text-muted)", letterSpacing: "0.22em", textTransform: "uppercase" }}>Admin Console</div>
          </div>
        </div>

        {/* Identity card — Platform Owner */}
        <button
          data-testid="admin-identity-card"
          onClick={() => setIdentityOpen((v) => !v)}
          style={{
            display: "flex", alignItems: "center", gap: 10, padding: "11px 12px",
            borderRadius: 11, background: "var(--pl-bg-2)", border: "1px solid var(--pl-border)",
            color: "var(--pl-text)", cursor: "pointer", marginBottom: 14, textAlign: "left",
            transition: "border-color 140ms",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(39,230,209,0.32)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--pl-border)"; }}
        >
          <span style={{
            width: 32, height: 32, borderRadius: 9, display: "grid", placeItems: "center",
            background: "linear-gradient(135deg, rgba(39,230,209,0.18), rgba(185,255,47,0.18))",
            color: "var(--pl-aqua)", flexShrink: 0,
          }}>
            <ShieldCheck size={16} strokeWidth={2.2} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 800, lineHeight: 1.1 }}>Platform Owner</div>
            <div style={{ fontSize: 10, color: "var(--pl-text-muted)", letterSpacing: "0.16em", textTransform: "uppercase", marginTop: 3 }}>
              Administrator Access
            </div>
          </div>
          <ChevronDown size={14} style={{ color: "var(--pl-text-muted)", transform: identityOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 160ms" }} />
        </button>

        {identityOpen && (
          <div data-testid="admin-identity-detail" style={{
            margin: "-10px 0 14px", padding: "10px 12px", borderRadius: 10,
            background: "var(--pl-bg)", border: "1px solid var(--pl-border)", fontSize: 11,
            display: "grid", gap: 6,
          }}>
            <Row k="Signed in" v={user?.email || "—"} />
            <Row k="Role" v={primaryRole} />
            <Row k="Environment" v={<span style={{ color: envTone, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase" }}>{env}</span>} />
          </div>
        )}

        {/* Primary navigation */}
        {NAV_GROUPS.map((group) => (
          <div key={group.title}>
            <div style={navHeaderStyle}>{group.title}</div>
            {group.items.map(({ to, label, icon: I, end }) => (
              <NavLink key={to} to={to} end={end} data-testid={`admin-nav-${label.toLowerCase().replace(/ /g, "-")}`}
                style={({ isActive }) => ({
                  display: "flex", alignItems: "center", gap: 10, padding: "9px 11px",
                  borderRadius: 9, marginBottom: 2,
                  color: isActive ? "var(--pl-aqua)" : "var(--pl-text)",
                  background: isActive ? "rgba(39,230,209,0.10)" : "transparent",
                  border: "1px solid " + (isActive ? "rgba(39,230,209,0.32)" : "transparent"),
                  fontSize: 12.5, fontWeight: 700, textDecoration: "none",
                })}>
                <I size={14} strokeWidth={1.9} />{label}
              </NavLink>
            ))}
          </div>
        ))}

        {/* Internal Tools — Admin Control highlighted card */}
        <div style={{ marginTop: 18 }}>
          <div style={navHeaderStyle}>Internal Tools</div>
          <NavLink
            to="/admin/control"
            data-testid="admin-nav-admin-control"
            style={({ isActive }) => ({
              display: "flex", alignItems: "center", gap: 11, padding: "12px 12px",
              borderRadius: 11,
              background: isActive
                ? "linear-gradient(135deg, rgba(217,164,82,0.22), rgba(160,99,38,0.22))"
                : "linear-gradient(135deg, rgba(217,164,82,0.14), rgba(160,99,38,0.14))",
              border: "1px solid " + (isActive ? "rgba(217,164,82,0.6)" : "rgba(217,164,82,0.34)"),
              color: "#fde0a8", textDecoration: "none",
              boxShadow: isActive ? "inset 0 0 0 1px rgba(217,164,82,0.18)" : "none",
            })}
          >
            <span style={{
              width: 30, height: 30, borderRadius: 8, display: "grid", placeItems: "center",
              background: "linear-gradient(135deg, #d9a452, #a06326)", color: "#1a0f02", flexShrink: 0,
              boxShadow: "0 4px 18px rgba(217,164,82,0.36)",
            }}>
              <ShieldCheck size={14} strokeWidth={2.4} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: "#fde0a8", lineHeight: 1.1 }}>Admin Control</div>
              <div style={{ fontSize: 10, color: "rgba(253,224,168,0.7)", marginTop: 3, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                AI Operations &amp; Training
              </div>
            </div>
          </NavLink>
        </div>

        <div style={{ flex: 1 }} />

        {/* Env + actions footer */}
        <div style={{ padding: 10, borderRadius: 10, background: "var(--pl-bg-2)", border: "1px solid var(--pl-border)", fontSize: 11, marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: envTone, marginBottom: 4 }}>
            <ShieldCheck size={12} /> <span style={{ fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase" }}>Env · {env}</span>
          </div>
          <div style={{ color: "var(--pl-text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user?.email}</div>
        </div>
        <button data-testid="admin-back-app" onClick={() => nav("/dashboard")} style={btnStyle}><ArrowLeft size={14} /> Back to app</button>
        <button data-testid="admin-logout" onClick={() => { logout(); nav("/login"); }} style={{ ...btnStyle, color: "#fda4af" }}><LogOut size={14} /> Sign out</button>
      </aside>
      <main style={{ minWidth: 0, padding: "22px 26px 80px", overflowY: "auto" }}>
        <Outlet />
      </main>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
      <span style={{ color: "var(--pl-text-muted)", fontWeight: 700 }}>{k}</span>
      <span>{v}</span>
    </div>
  );
}

const navHeaderStyle = {
  fontSize: 9, fontWeight: 800, letterSpacing: "0.22em", textTransform: "uppercase",
  color: "var(--pl-text-muted)", padding: "10px 11px 6px",
};

const btnStyle = {
  display: "inline-flex", alignItems: "center", gap: 8, height: 32, padding: "0 10px",
  background: "transparent", border: "1px solid var(--pl-border)", borderRadius: 9,
  color: "var(--pl-text)", fontSize: 12, fontWeight: 700, cursor: "pointer", marginTop: 6,
};
